import { cueUrl, trackUrl, type Cue, type Track } from './cues.js'

/**
 * The bit that actually makes a noise.
 *
 * Kept apart from React so the awkward parts — a browser that refuses to play
 * until somebody has pressed something, one loop at a time, a missing file —
 * can be tested without rendering anything, and so that jsdom (which has no
 * audio at all: `HTMLMediaElement.play` is not implemented and throws) can be
 * handed a stand-in.
 */
export interface Sounds {
  play(cue: Cue): void
  music(track: Track | null): void
  /** Somebody has pressed something, so the browser will let us start. */
  unlock(): void
  stop(): void
}

/** What a player needs from an audio element, and no more. */
export interface Playable {
  play(): Promise<void> | void
  pause(): void
  currentTime: number
  loop: boolean
  volume: number
}

export interface PlayerOptions {
  /** Made per URL, so a test can hand over something that makes no sound. */
  readonly create?: (url: string) => Playable
  /**
   * Where a sound lives.
   *
   * Handed in rather than reached for, so the player can be built and tested
   * with no sound files in the repo at all — which is how it was written, and
   * which would otherwise have to wait on somebody making twenty-eight of
   * them. It also keeps the bundler's glob out of the part that has the
   * awkward logic in it.
   */
  readonly find?: {
    readonly cue: (cue: Cue) => string | undefined
    readonly track: (track: Track) => string | undefined
  }
  /** Music sits under the effects rather than over them. */
  readonly musicVolume?: number
  readonly effectVolume?: number
}

function realAudio(url: string): Playable {
  return new Audio(url)
}

/**
 * Volumes chosen so an effect never has to fight the music it lands on.
 *
 * The files are mastered to sit together — music around −18 LUFS, effects
 * peaking under −6 dBFS — so this is a second, gentler hand on the same
 * balance rather than the whole of it.
 */
const MUSIC_VOLUME = 0.45
const EFFECT_VOLUME = 0.8

export function createPlayer(options: PlayerOptions = {}): Sounds {
  const create = options.create ?? realAudio
  const find = options.find ?? { cue: cueUrl, track: trackUrl }
  const musicVolume = options.musicVolume ?? MUSIC_VOLUME
  const effectVolume = options.effectVolume ?? EFFECT_VOLUME

  /*
   * One element per cue, reused.
   *
   * A card flip fires twenty times in a round, and a fresh element each time
   * is twenty things for the browser to keep. Reusing one means a cue cuts
   * itself off if it is asked for twice in a moment — which for sounds this
   * short is not something anybody can hear.
   */
  const effects = new Map<Cue, Playable>()

  let playing: { track: Track; sound: Playable } | null = null
  let wanted: Track | null = null
  let unlocked = false

  /*
   * Dropped on the floor, deliberately.
   *
   * Until somebody has pressed a key or touched the screen, every browser
   * refuses to play and rejects the promise. The Pi boots straight into the
   * menu with nobody having pressed anything, so that refusal is the ordinary
   * case rather than an error — and an unhandled rejection in the console of a
   * kiosk is noise nobody will ever read.
   */
  function attempt(sound: Playable): void {
    try {
      const started = sound.play()
      if (started && typeof started.catch === 'function') started.catch(() => undefined)
    } catch {
      // Some browsers throw rather than reject. Same answer.
    }
  }

  function startMusic(): void {
    if (!playing || !unlocked) return
    attempt(playing.sound)
  }

  return {
    play(cue) {
      const url = find.cue(cue)
      // No file for it yet, or nobody has pressed anything: silence either way.
      if (!url || !unlocked) return

      let sound = effects.get(cue)
      if (!sound) {
        sound = create(url)
        sound.volume = effectVolume
        effects.set(cue, sound)
      }

      sound.currentTime = 0
      attempt(sound)
    },

    music(track) {
      wanted = track

      // Already the one playing, so leave it alone rather than restart it at
      // the top — moving between two screens that want the same loop should
      // not be audible.
      if (playing?.track === track) return

      playing?.sound.pause()
      playing = null

      if (track === null) return

      const url = find.track(track)
      if (!url) return

      const sound = create(url)
      sound.loop = true
      sound.volume = musicVolume
      playing = { track, sound }

      startMusic()
    },

    unlock() {
      if (unlocked) return
      unlocked = true

      // Whatever was asked for before anybody pressed anything starts now.
      if (playing) startMusic()
      else if (wanted !== null) this.music(wanted)
    },

    stop() {
      playing?.sound.pause()
      playing = null
      for (const sound of effects.values()) sound.pause()
      effects.clear()
    },
  }
}

/** A player for every screen that is not the television: does nothing at all. */
export function silentPlayer(): Sounds {
  return {
    play: () => undefined,
    music: () => undefined,
    unlock: () => undefined,
    stop: () => undefined,
  }
}
